/* Copyright (C) 2019 SmartboxTv S.A. - All rights reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Erasmo Marín <emarin@smartboxtv.com>
 */

import { observable, action, computed, flow, ObservableMap } from 'mobx';
import {
    generateList,
    generateListRefresh,
    generateCreate,
    generateUpdate,
    generateListMore,
    generateDelete,
} from './actionCreator';
import map from 'lodash/map';
import get from 'lodash/get';
import fetch from '../fetch';
import ErrorsStore from './errorsStore';
import _ from '../translations';

class TypesStore {
    @observable collection = new ObservableMap();
    @observable details = new ObservableMap();

    constructor() {
        this.baseUrl = '/api/generic/types';
    }

    @action
    reset = () => {
        this.collection.clear();
        this.details.clear();
    };

    /*FIXME: There is no service to get detail, so we filter by id here*/
    @action.bound
    detail = flow(function*({ _id = false } = {}) {
        this.details.set(_id, {
            loading: true,
        });

        const url = `${this.baseUrl}?filterBy-_id=${_id}`;

        try {
            let detail = yield fetch.get({ url, params: {} });
            detail = get(detail, 'items[0]');
            if (detail.error) {
                ErrorsStore.addError(detail, _('There was a problem fetching content type'));
            }
            this.details.set(_id, detail);
        } catch (e) {
            ErrorsStore.addError(e, _('There was a problem fetching content type'));
        }
    });

    @action.bound
    list = flow(function*({ hash, params } = {}) {
        return yield generateList({
            hash,
            params,
            url: this.baseUrl,
            store: this,
            customError: _('There was a problem listing content type'),
        })();
    });

    @action.bound
    listRefresh = flow(function*({ hash, params = {}, maxPagesToRefresh } = {}) {
        return yield generateListRefresh({
            hash,
            params,
            url: this.baseUrl,
            store: this,
            customError: _('There was a problem listing types'),
            maxPagesToRefresh,
        })();
    });

    @action.bound
    listAll = flow(function*() {
        return yield generateList({
            hash: 'all',
            params: { paginate: false },
            url: this.baseUrl,
            store: this,
            customError: _('There was a problem listing content type'),
        })();
    });

    @action.bound
    listAllForContent = flow(function*() {
        return yield generateList({
            hash: 'allContents',
            params: { paginate: false, 'filterBy-key': 'contents' },
            url: this.baseUrl,
            store: this,
            customError: _('There was a problem listing content type'),
        })();
    });

    @action.bound
    listAllForStaff = flow(function*() {
        return yield generateList({
            hash: 'allStaff',
            params: { paginate: false, 'filterBy-key': 'roles-staff' },
            url: this.baseUrl,
            store: this,
            customError: _('There was a problem listing staff type'),
        })();
    });

    @action.bound
    listMore = flow(function*({ hash, params = {} } = {}) {
        return yield generateListMore({
            hash,
            params,
            url: this.baseUrl,
            store: this,
            customError: _('There was a problem listing content type'),
        })();
    });

    @action.bound
    create = flow(function*({ item = {} } = {}) {
        return yield generateCreate({
            item,
            url: this.baseUrl,
            store: this,
            customError: _('There was a problem creating content type'),
        })();
    });

    @action.bound
    update = flow(function*({ items, item = {} } = {}) {
        return yield generateUpdate({
            items,
            item,
            url: `${this.baseUrl}/multiple`,
            store: this,
            customError: _('There was a problem updating content type'),
        })();
    });

    @action.bound
    delete = flow(function*({ items, _id = false, type = 'logical' } = {}) {
        return yield generateDelete({
            items,
            _id,
            type,
            url: `${this.baseUrl}/multiple`,
            store: this,
            customError: _('There was a problem deleting content type'),
        })();
    });

    @computed
    get forContentSelect() {
        const page = this.collection.get('allContents');
        const items = map(page ? page.items : {}, item => ({
            text: item.name.original,
            value: item._id,
        }));
        return items;
    }

    @computed
    get forStaffSelect() {
        const page = this.collection.get('allStaff');
        const items = map(page ? page.items : {}, item => ({
            text: item.name.original,
            value: item._id,
        }));
        return items;
    }
}

export default new TypesStore();
